import { computed, unref } from 'vue';
import { useUISettings } from './useUISettings';

const UI_SETTINGS_KEY = 'favorite_whatsapp_templates_by_inbox';

export const getWhatsAppTemplateFavoriteKey = template => {
  const name = template?.name || '';
  const language = template?.language || 'en';
  return `${name}::${language}`;
};

export function useFavoriteWhatsAppTemplates(inboxId) {
  const { uiSettings, updateUISettings } = useUISettings();

  const favoriteMap = computed(
    () => uiSettings.value?.[UI_SETTINGS_KEY] || {}
  );

  const currentInboxKey = computed(() => {
    const value = unref(inboxId);
    return value ? String(value) : null;
  });

  const favoriteKeys = computed(() => {
    if (!currentInboxKey.value) return [];
    return favoriteMap.value[currentInboxKey.value] || [];
  });

  const isFavoriteTemplate = template =>
    favoriteKeys.value.includes(getWhatsAppTemplateFavoriteKey(template));

  const toggleFavoriteTemplate = template => {
    if (!currentInboxKey.value || !template?.name) return;

    const templateKey = getWhatsAppTemplateFavoriteKey(template);
    const currentValues = favoriteMap.value[currentInboxKey.value] || [];
    const updatedValues = isFavoriteTemplate(template)
      ? currentValues.filter(value => value !== templateKey)
      : [templateKey, ...currentValues.filter(value => value !== templateKey)];

    updateUISettings({
      [UI_SETTINGS_KEY]: {
        ...favoriteMap.value,
        [currentInboxKey.value]: updatedValues.slice(0, 12),
      },
    });
  };

  const sortTemplatesByFavorite = templates =>
    [...templates].sort((left, right) => {
      const favoriteDiff =
        Number(isFavoriteTemplate(right)) - Number(isFavoriteTemplate(left));
      if (favoriteDiff !== 0) return favoriteDiff;

      return String(left?.name || '').localeCompare(String(right?.name || ''));
    });

  const favoriteTemplatesFrom = templates =>
    sortTemplatesByFavorite(templates).filter(template =>
      isFavoriteTemplate(template)
    );

  return {
    favoriteKeys,
    isFavoriteTemplate,
    toggleFavoriteTemplate,
    sortTemplatesByFavorite,
    favoriteTemplatesFrom,
  };
}
